import { useFormik } from "formik";
import { Button, Dropdown, Form, Modal } from "react-bootstrap";
import {
  ANNOUNCEMENT_STATUS,
  ANNOUNCEMENT_TYPE,
} from "../../../services/flitEnum";
import * as Yup from "yup";
import { useSelector } from "react-redux";
import { IoCloseOutline } from "react-icons/io5";
import React, { useContext, useEffect, useState } from "react";
import { TailSpin } from "react-loader-spinner";
import { FlitServiceContext } from "../../../services/flitService";
import { getAnnouncementTypeCssClasses, getAnnouncementStatusCssClasses } from "./announcementHelper";
import cf from '../../../services/codeformatter'
import { useIntl } from "react-intl";

export const AnnouncementCreateModal = ({ fetchAnnouncements }) => {
  const MAX_TITLE_LENGTH = 1024;
  const { flitSvc } = useContext(FlitServiceContext);
  const { user } = useSelector(state => state.auth);
  const [open, setOpen] = useState(false);
  const [userCanCreate, setUserCanCreate] = useState(false);
  const intl = useIntl();
  useEffect(() => {
    if (user) {
      const isAdmin = user.userRole.some(
        userRole => userRole.role === "DEVELOPER" || userRole.role === "MANAGER"
      );
      setUserCanCreate(isAdmin);
    }
  }, [user]);

  const validationSchema = Yup.object().shape({
    title: Yup.string()
      .required("Title is required")
      .min(1, "Title must be at least 1 character")
      .max(
        MAX_TITLE_LENGTH,
        `Title must be at most ${MAX_TITLE_LENGTH} characters`
      ),
    message: Yup.string(),
    type: Yup.mixed()
      .oneOf(Object.values(ANNOUNCEMENT_TYPE), "Invalid announcement type")
      .required("Type is required"),
    status: Yup.string().oneOf(
      Object.values(ANNOUNCEMENT_STATUS),
      "Invalid announcement status"
    ),
  });

  const formik = useFormik({
    initialValues: {
      title: "",
      message: "",
      type: ANNOUNCEMENT_TYPE.GENERAL,
      status: ANNOUNCEMENT_STATUS.DRAFT,
      submittedById: user.id,
    },
    validationSchema,
    enableReinitialize: true,
    onSubmit: async (values, { setSubmitting }) => {
      setSubmitting(true);
      try {
        await flitSvc.createAnnouncement(values);
        formik.resetForm();
        fetchAnnouncements();
        setOpen(false);
      } catch (error) {
        console.error("Error updating announcement", error);
      } finally {
        setSubmitting(false);
      }
    },
  });

  useEffect(() => {
    if (!open) {
      formik.resetForm();
    }
  }, [formik, open]);

  return (
    <>
      <div className="d-flex align-items-center justify-content-end">
        {/* <span className="h1 font-weight-bold">Announcements</span> */}
        {userCanCreate && (
          <Button variant="primary" size="sm" onClick={() => setOpen(true)} className="mr-3">
                    {intl.formatMessage({ id: "NEW_ANNOUNCEMENT" })}
            </Button>
        )}
      </div>
      <Modal show={open} onHide={() => setOpen(false)}>
        <Modal.Header>
          <Modal.Title>
            <span>{intl.formatMessage({ id: "NEW_ANNOUNCEMENT" })}</span>
          </Modal.Title>
          <button
            className="close"
            style={{ border: "transparent" }}
            onClick={() => setOpen(false)}
          >
            <IoCloseOutline />
          </button>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={formik.handleSubmit}>
            <div className="d-flex g-3 align-items-center mb-4">
              <div className="g-3 d-flex align-items-start ">
                <Dropdown>
                  <Dropdown.Toggle variant="success" id="dropdown-basic">
                    {cf.format('AnnouncementType', formik.values.type, intl.locale)}
                  </Dropdown.Toggle>
                  <Dropdown.Menu>
                    {Object.keys(ANNOUNCEMENT_TYPE).map(at => (
                      <Dropdown.Item
                        key={at}
                        className="w-44"
                        style={{ minWidth: "200px", maxWidth: "100%" }}
                        onClick={() =>
                          formik.setFieldValue("type", ANNOUNCEMENT_TYPE[at])
                        }
                      >
                        {cf.format('AnnouncementType', ANNOUNCEMENT_TYPE[at], intl.locale)}
                      </Dropdown.Item>
                    ))}
                  </Dropdown.Menu>
                </Dropdown>
                {formik.touched.type && formik.errors.type && (
                  <div className="text-danger small">{formik.errors.type}</div>
                )}
              </div>

              <div className="g-3 d-flex align-items-start ml-2 ">
                <Dropdown>
                  <Dropdown.Toggle variant="default" id="dropdown-basic">
                    {cf.format('AnnouncementStatus', formik.values.status, intl.locale)}
                  </Dropdown.Toggle>
                  <Dropdown.Menu>
                    {Object.keys(ANNOUNCEMENT_STATUS).map(at => (
                      <Dropdown.Item
                        key={at}
                        className="w-44"
                        style={{ minWidth: "200px", maxWidth: "100%" }}
                        onClick={() =>
                          formik.setFieldValue(
                            "status",
                            ANNOUNCEMENT_STATUS[at]
                          )
                        }
                      >
                        {cf.format('AnnouncementStatus', ANNOUNCEMENT_STATUS[at], intl.locale)}
                      </Dropdown.Item>
                    ))}
                  </Dropdown.Menu>
                </Dropdown>
                {formik.touched.status && formik.errors.type && (
                  <div className="text-danger small">{formik.errors.type}</div>
                )}
              </div>
            </div>

            <div className="d-flex flex-column gap-3 mb-4">
              <Form.Label>{intl.formatMessage({ id: "TITLE" })}</Form.Label>
              <Form.Control
                type="text"
                style={{ width: "100%", marginBottom: "4px" }}
                name="title"
                value={formik.values.title}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                maxLength={MAX_TITLE_LENGTH}
                isInvalid={formik.touched.title && !!formik.errors.title}
              />
              <Form.Control.Feedback type="invalid">
                {formik.errors.title}
              </Form.Control.Feedback>
            </div>
            <div className="mb-4">
              <Form.Label>{intl.formatMessage({ id: "MESSAGE" })}</Form.Label>
              <Form.Control
                style={{
                  width: "100%",
                  minHeight: "12rem",
                  marginBottom: "4px",
                }}
                name="message"
                value={formik.values.message}
                as="textarea"
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                isInvalid={formik.touched.message && !!formik.errors.message}
              />
              <Form.Control.Feedback type="invalid">
                {formik.errors.message}
              </Form.Control.Feedback>
            </div>
            <div className="d-flex align-items-center justify-content-end">
              <button
                className="btn btn-primary btn-elevate d-flex justify-content-center"
                type="button"
                style={{ width: "80px" }}
                onClick={formik.handleSubmit}
              >
                {formik.isSubmitting ? (
                  <TailSpin height={12} width={12} color="black" />
                ) : (
                  intl.formatMessage({ id: "SUBMIT_BUTTON" })
                )}
              </button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>
    </>
  );
};
